/**
 * Cropping a picture (Miro's double click): the whole picture shown around the part kept, its
 * sides and corners dragged, the part kept written as `style.crop` (one undo) or dropped.
 */
import { createSignal } from "solid-js";
import { toast } from "../../shared/toast";
import type { BoardCtx } from "./context";
import { asPatch, cropOf, cropped, cropText, parseCrop, rounded, wholePicture } from "./geometry";
import type { BoardPart, Cropping } from "./types";

/** The crop's members of the board service. */
export function cropPart(ctx: BoardCtx): BoardPart<"cropping" | "startCrop" | "cropBy" | "cropPressEnd" | "resetCrop" | "endCrop" | "uncrop"> {
  const { actions, view } = ctx.deps;
  const [cropping, setCropping] = createSignal<Cropping | null>(null);
  /** Where a crop drag began. */
  let cropStart: Cropping | null = null;
  return {
    cropping,
    startCrop(id) {
      const node = ctx.nodeOf(id);
      const place = ctx.content().places.get(id);
      if (!node || !place || ctx.deps.kinds.board(node.kind).element !== "image") return;
      if (place.rotate) return void toast("Turn the picture back straight to crop it.");
      view.selectNode(id);
      const whole = wholePicture(place, parseCrop(node.style?.crop));
      setCropping({ id, whole, box: { x: place.x, y: place.y, w: place.w, h: place.h } });
    },
    cropBy(handle, dx, dy) {
      const now = cropping();
      if (!now) return;
      cropStart ??= now;
      const next = cropped(cropStart.whole, cropStart.box, handle, dx, dy);
      setCropping({ id: now.id, ...next });
    },
    cropPressEnd() {
      cropStart = null;
    },
    resetCrop() {
      const now = cropping();
      if (now) setCropping({ ...now, box: { ...now.whole } });
    },
    async endCrop(keep) {
      const now = cropping();
      cropStart = null;
      setCropping(null);
      if (!keep || !now) return;
      const place = ctx.content().places.get(now.id);
      if (!place) return;
      const box = rounded({ ...place, ...now.box });
      const text = cropText(cropOf(now.whole, now.box));
      if (text === (ctx.nodeOf(now.id)?.style?.crop ?? "") && box.x === place.x && box.y === place.y && box.w === place.w && box.h === place.h) return;
      await actions.updateNode(now.id, { board: asPatch(box), style: { crop: text } });
    },
    async uncrop() {
      const id = ctx.selectedNodes()[0];
      const node = id ? ctx.nodeOf(id) : undefined;
      const place = id ? ctx.content().places.get(id) : undefined;
      if (!id || !node || !place || !parseCrop(node.style?.crop)) return;
      const whole = wholePicture(place, parseCrop(node.style?.crop));
      await actions.updateNode(id, { board: asPatch(rounded({ ...place, ...whole })), style: { crop: "" } });
    },
  };
}
